import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { DatabaseService } from 'src/app/features/database/components/services/database-service';
import { SnackbarV3Service } from '@gea/digital-ui-lib';
import { environment } from 'src/app/core/environments/environment';
import { AuthService } from 'src/app/core/services/auth.service';
import { PermissionService } from 'src/app/core/services/permission.service';

let redirectTriggered = false;
let snackbarShown = false;
const loginRedirectMessageKey = 'ceh-login-redirect-message';
const redirectBypassUrls = [
  '/Auth/Login',
];

const shouldBypassRedirect = (url: string): boolean => {
  return redirectBypassUrls.some((bypassUrl) => url.includes(bypassUrl));
};

const redirectToLogin = (router: Router): void => {
  const loginUrl = router.serializeUrl(router.createUrlTree(['/login']));
  window.location.replace(loginUrl);
};

const setLoginRedirectMessage = (response: HttpErrorResponse): void => {
  let detail = 'Access denied';

  if (typeof response?.error === 'string') {
    detail = response.error;
  } else if (response?.status === 401) {
    detail = 'Session expired';
  }

  sessionStorage.setItem(
    loginRedirectMessageKey,
    JSON.stringify({
      detail,
      summary: response?.statusText || 'Error',
      severity: 'error',
    }),
  );
};

export const ErrorInterceptor: HttpInterceptorFn = (req, next) => {
  if (environment.name.includes('cloud') || shouldBypassRedirect(req.url)) {
    return next(req);
  }

  const router = inject(Router);
  const databaseService = inject(DatabaseService);
  const snackbarService = inject(SnackbarV3Service);
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);

  return next(req).pipe(
    catchError((response: HttpErrorResponse) => {

      const resetFlags = () => {
        setTimeout(() => {
          redirectTriggered = false;
          snackbarShown = false;
        }, 500);
      };

      const isDatabaseError =
        response.status === 500 &&
        typeof response.error === 'string' &&
        response.error.includes('ConnectionString property has not been initialized');

      if (isDatabaseError) {
        handleDatabaseError(databaseService, router, snackbarService, permissionService, resetFlags);
        return throwError(() => response);
      }

      if (response.status === 401) {
        handleUnauthorizedError(response, snackbarService, authService, router, resetFlags);
        return throwError(() => response);
      }

      return throwError(() => response);
    }),
  );
};

function handleDatabaseError(
  databaseService: DatabaseService,
  router: Router,
  snackbarService: SnackbarV3Service,
  permissionService: PermissionService,
  resetFlags: () => void,
): void {
  if (!redirectTriggered) {
    redirectTriggered = true;
    databaseService.isConnected$.next(false);
    router.navigate(['/database/open-database']);
    resetFlags();
  }

  const isOnlyAdmin = permissionService.isOnlyAdmin();
  if (!snackbarShown && !isOnlyAdmin) {
    snackbarShown = true;
    snackbarService.add({
      detail: 'You must select a database to continue.',
      summary: 'Info',
      severity: 'info',
    });
    resetFlags();
  }
}

function handleUnauthorizedError(
  response: HttpErrorResponse,
  snackbarService: SnackbarV3Service,
  authService: AuthService,
  router: Router,
  resetFlags: () => void,
): void {
  if (redirectTriggered) {
    return;
  }
  redirectTriggered = true;
  const detail = typeof response?.error === 'string' ? response.error : 'Session expired';
  snackbarService.add({
    detail,
    summary: 'Error',
    severity: 'error',
  });
  setLoginRedirectMessage(response);
  authService.logout();
  redirectToLogin(router);
  resetFlags();
}
